import { AppLayout } from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { Shield, Monitor, MapPin, Clock, Trash2, RotateCcw, User } from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";

interface SessionEntry {
  id: string;
  userId: string;
  userAgent: string | null;
  ipAddress: string | null;
  createdAt: string;
  expiresAt: string;
  user: {
    firstName: string;
    lastName: string;
    email: string;
    role: string;
  };
}

function parseDevice(userAgent: string | null): string {
  if (!userAgent) return "Unknown device";
  if (/mobile/i.test(userAgent)) return "Mobile browser";
  if (/tablet/i.test(userAgent)) return "Tablet browser";
  if (/chrome/i.test(userAgent)) return "Chrome";
  if (/firefox/i.test(userAgent)) return "Firefox";
  if (/safari/i.test(userAgent)) return "Safari";
  if (/edge/i.test(userAgent)) return "Edge";
  return "Browser";
}

function roleBadgeVariant(role: string) {
  if (role === "ADMIN") return "default";
  if (role === "TRAINER") return "secondary";
  return "outline";
}

export default function AdminSessions() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [revoking, setRevoking] = useState<string | null>(null);

  const { data: sessions = [], isLoading, isError, error: queryError, refetch } = useQuery<SessionEntry[]>({
    queryKey: ["admin-sessions"],
    queryFn: () => apiFetch<SessionEntry[]>("/admin/sessions"),
  });

  async function revokeSession(id: string) {
    setRevoking(id);
    try {
      await apiFetch(`/admin/sessions/${id}`, { method: "DELETE" });
      toast({ title: "Session revoked", description: "The login session has been terminated." });
      queryClient.invalidateQueries({ queryKey: ["admin-sessions"] });
    } catch (e: any) {
      toast({ title: "Failed to revoke session", description: e.message, variant: "destructive" });
    } finally {
      setRevoking(null);
    }
  }

  const groupedByUser = sessions.reduce<Record<string, SessionEntry[]>>((acc, s) => {
    const key = s.userId;
    if (!acc[key]) acc[key] = [];
    acc[key].push(s);
    return acc;
  }, {});

  const uniqueUsers = Object.keys(groupedByUser).length;

  return (
    <AppLayout>
      <div className="flex flex-col gap-8 pb-12">
        <div>
          <h1 className="text-4xl font-display font-bold text-white mb-2">Active Sessions</h1>
          <p className="text-muted-foreground">Audit and revoke active login sessions across your gym members</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="glass-panel border-white/5">
            <CardHeader className="pb-2">
              <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Total Active Sessions</CardDescription>
              <CardTitle className="text-3xl font-mono text-white">{isLoading ? "—" : sessions.length}</CardTitle>
            </CardHeader>
          </Card>
          <Card className="glass-panel border-white/5">
            <CardHeader className="pb-2">
              <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Unique Users Online</CardDescription>
              <CardTitle className="text-3xl font-mono text-primary">{isLoading ? "—" : uniqueUsers}</CardTitle>
            </CardHeader>
          </Card>
          <Card className="glass-panel border-white/5">
            <CardHeader className="pb-2">
              <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Session Expiry Window</CardDescription>
              <CardTitle className="text-3xl font-mono text-white">30 days</CardTitle>
            </CardHeader>
          </Card>
        </div>

        {isError ? (
          <div className="p-12 text-center rounded-2xl border border-dashed border-red-500/20 bg-red-500/5">
            <Shield className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <p className="text-white font-medium text-lg">Failed to load sessions</p>
            <p className="text-sm text-muted-foreground mt-2">{(queryError as any)?.message || "An error occurred"}</p>
            <Button onClick={() => refetch()} variant="ghost" className="mt-4 text-primary">
              <RotateCcw className="w-4 h-4 mr-2" /> Retry
            </Button>
          </div>
        ) : isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => <div key={i} className="h-28 bg-white/5 rounded-xl animate-pulse" />)}
          </div>
        ) : sessions.length === 0 ? (
          <div className="p-12 text-center rounded-2xl border border-dashed border-white/10 bg-white/5">
            <Shield className="w-12 h-12 text-primary/60 mx-auto mb-4" />
            <p className="text-white font-medium text-lg">No active sessions found</p>
            <p className="text-sm text-muted-foreground mt-2">Active login sessions for your gym members will appear here</p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedByUser).map(([, userSessions]) => {
              const { user } = userSessions[0];
              return (
                <Card key={userSessions[0].userId} className="glass-panel border-white/5">
                  <CardHeader className="pb-3 border-b border-white/5">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        <User className="w-4 h-4 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-semibold truncate">{user.firstName} {user.lastName}</p>
                        <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                      </div>
                      <Badge variant={roleBadgeVariant(user.role)} className="text-xs capitalize shrink-0">
                        {user.role.toLowerCase()}
                      </Badge>
                      <span className="text-xs text-muted-foreground shrink-0">
                        {userSessions.length} session{userSessions.length !== 1 ? "s" : ""}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0 divide-y divide-white/5">
                    {userSessions.map(session => (
                      <div key={session.id} className="py-4 flex flex-col sm:flex-row sm:items-center gap-3">
                        <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Monitor className="w-3.5 h-3.5 shrink-0" />
                            <span className="truncate">{parseDevice(session.userAgent)}</span>
                          </div>
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <MapPin className="w-3.5 h-3.5 shrink-0" />
                            <span className="truncate font-mono text-xs">{session.ipAddress ?? "Unknown IP"}</span>
                          </div>
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Clock className="w-3.5 h-3.5 shrink-0" />
                            <span className="truncate text-xs" title={format(new Date(session.createdAt), "PPpp")}>
                              Signed in {formatDistanceToNow(new Date(session.createdAt), { addSuffix: true })}
                            </span>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-400 hover:bg-red-500/10 hover:text-red-300 gap-1.5 shrink-0"
                          onClick={() => revokeSession(session.id)}
                          disabled={revoking === session.id}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          {revoking === session.id ? "Revoking…" : "Revoke"}
                        </Button>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
